import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  addMonths,
  daysOfMonth,
  formatDay,
  formatLongDay,
  formatMonthTitle,
  monthOf,
  todayInRome,
} from './calendar-dates';
import {
  CALENDAR_ITEM_KINDS,
  groupItemsByDay,
  type CalendarItem,
  type CalendarItemKind,
  type VisitState,
} from './calendar-items';

// Resolution R11: literal maps, never template-string `t()` keys.
const KIND_LABEL_KEY = {
  actual: 'calendar.kindActual',
  reserved: 'calendar.kindReserved',
  appointment: 'calendar.kindAppointment',
  visit: 'calendar.kindVisit',
  windowStart: 'calendar.kindWindowStart',
  windowEnd: 'calendar.kindWindowEnd',
  poolFree: 'calendar.kindPoolFree',
} as const satisfies Record<CalendarItemKind, string>;
const VISIT_STATE_LABEL_KEY = {
  PLANNED: 'schedule.statePLANNED',
  SCHEDULED: 'schedule.stateSCHEDULED',
  COMPLETED: 'schedule.stateCOMPLETED',
  MISSED: 'schedule.stateMISSED',
  CANCELLED: 'schedule.stateCANCELLED',
} as const satisfies Record<VisitState, string>;
const TIMEPOINT_TYPE_LABEL_KEY = {
  TREATMENT_ANCHOR: 'schedule.typeAnchor',
  FOLLOW_UP: 'schedule.typeFollowUp',
} as const;

// Tones of the shared `.status-badge`: colour always comes with the text label beside it.
const KIND_TONE = {
  actual: 'success',
  reserved: 'info',
  appointment: 'info',
  windowStart: '',
  windowEnd: '',
  poolFree: 'warning',
} as const;
const VISIT_STATE_TONE = {
  PLANNED: '',
  SCHEDULED: 'info',
  COMPLETED: 'success',
  MISSED: 'error',
  CANCELLED: '',
} as const satisfies Record<VisitState, string>;
const VISIT_STATES = ['PLANNED', 'SCHEDULED', 'COMPLETED', 'MISSED', 'CANCELLED'] as const;

function itemTone(item: CalendarItem): string {
  return item.kind === 'visit' ? VISIT_STATE_TONE[item.state] : KIND_TONE[item.kind];
}

function useItemText() {
  const { t } = useTranslation('clinical-study');

  const visitName = (item: Extract<CalendarItem, { visitId: string }>) =>
    t('calendar.visitName', {
      type: t(TIMEPOINT_TYPE_LABEL_KEY[item.timepointType]),
      offset: item.offsetDays >= 0 ? `+${item.offsetDays}` : String(item.offsetDays),
    });

  /** The full text of an item, as the day list shows it. */
  const full = (item: CalendarItem): string => {
    switch (item.kind) {
      case 'poolFree':
        return t('calendar.poolFree', { free: item.free });
      case 'actual':
      case 'reserved':
        return `${item.subjectCode} · ${t(KIND_LABEL_KEY[item.kind])}`;
      case 'visit': {
        const parts = [
          item.subjectCode,
          visitName(item),
          t(VISIT_STATE_LABEL_KEY[item.state]),
          t('calendar.windowRange', {
            start: formatDay(item.windowStart),
            end: formatDay(item.windowEnd),
          }),
        ];
        if (item.appointmentDate !== null) {
          parts.push(t('calendar.appointmentOn', { date: formatDay(item.appointmentDate) }));
        }
        if (item.outOfWindow === true) {
          parts.push(t('schedule.outOfWindowBadge'));
        }
        return parts.join(' · ');
      }
      case 'appointment':
        return [
          item.subjectCode,
          t(KIND_LABEL_KEY.appointment),
          visitName(item),
          t(VISIT_STATE_LABEL_KEY[item.state]),
          ...(item.outOfWindow === true ? [t('schedule.outOfWindowBadge')] : []),
        ].join(' · ');
      case 'windowStart':
      case 'windowEnd':
        return [
          item.subjectCode,
          t(KIND_LABEL_KEY[item.kind]),
          visitName(item),
          t('calendar.targetOn', { date: formatDay(item.targetDate) }),
        ].join(' · ');
    }
  };

  return { full };
}

function itemSubjectId(item: CalendarItem): string | null {
  return item.kind === 'poolFree' ? null : item.subjectId;
}

function Legend() {
  const { t } = useTranslation('clinical-study');
  return (
    <div className="space-y-2 text-xs">
      <h4 className="font-semibold">{t('calendar.legendTitle')}</h4>
      <ul className="flex flex-wrap gap-2" aria-label={t('calendar.legendTitle')}>
        {CALENDAR_ITEM_KINDS.filter((kind) => kind !== 'visit').map((kind) => (
          <li key={kind} className={`status-badge ${KIND_TONE[kind]}`}>
            {t(KIND_LABEL_KEY[kind])}
          </li>
        ))}
      </ul>
      <p>{t('calendar.visitStatesLegend')}</p>
      <ul className="flex flex-wrap gap-2" aria-label={t('calendar.visitStatesLegend')}>
        {VISIT_STATES.map((state) => (
          <li key={state} className={`status-badge ${VISIT_STATE_TONE[state]}`}>
            {t(VISIT_STATE_LABEL_KEY[state])}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DayItemList({
  items,
  onOpenSubject,
}: {
  items: readonly CalendarItem[];
  onOpenSubject: ((subjectId: string) => void) | undefined;
}) {
  const { full } = useItemText();
  return (
    <ul className="space-y-1">
      {items.map((item) => {
        const subjectId = itemSubjectId(item);
        const badge = <span className={`status-badge ${itemTone(item)}`}>{full(item)}</span>;
        return (
          <li key={item.key}>
            {subjectId !== null && onOpenSubject !== undefined ? (
              <button
                type="button"
                onClick={() => onOpenSubject(subjectId)}
                className="hover:bg-accent focus-visible:ring-ring flex min-h-11 w-full items-center
                  rounded-md px-2 text-left focus-visible:ring-2 focus-visible:outline-none
                  [&_.status-badge]:whitespace-normal"
              >
                {badge}
              </button>
            ) : (
              <div className="flex min-h-11 items-center px-2 [&_.status-badge]:whitespace-normal">
                {badge}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The clinical-study calendar (#893): an agenda of one month, grouped by day, the same on desktop
 * and phone, with previous/next month and «Oggi». It only presents `items`; the study and the
 * subject views decide what goes in. Each item carries its text label next to its colour, and an
 * item that belongs to a subject opens it when `onOpenSubject` is given.
 */
export function ClinicalCalendar({
  items,
  onOpenSubject,
  idPrefix,
  loading = false,
}: {
  items: readonly CalendarItem[];
  /** While reads are pending, an empty month says nothing: «no items» would be a false empty
   * state (the host shows the loading status). */
  loading?: boolean;
  onOpenSubject?: (subjectId: string) => void;
  /** Keeps ids unique when two calendars share a page. */
  idPrefix: string;
}) {
  const { t, i18n } = useTranslation('clinical-study');
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const today = todayInRome();
  const [month, setMonth] = useState(monthOf(today));

  const byDay = useMemo(() => groupItemsByDay(items), [items]);
  const titleId = `${idPrefix}-calendar-title`;
  const monthDaysWithItems = daysOfMonth(month).filter((day) => byDay.has(day));

  const dayName = (day: string) => {
    const name = formatLongDay(day, locale);
    return day === today ? `${name} (${t('calendar.todayMarker')})` : name;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('calendar.previousMonth')}
          onClick={() => setMonth(addMonths(month, -1))}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <h4
          id={titleId}
          className="min-w-40 text-center font-semibold capitalize"
          aria-live="polite"
        >
          {formatMonthTitle(month, locale)}
        </h4>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('calendar.nextMonth')}
          onClick={() => setMonth(addMonths(month, 1))}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => setMonth(monthOf(today))}
        >
          {t('calendar.today')}
        </Button>
      </div>

      {monthDaysWithItems.length === 0 ? (
        !loading && <p className="text-sm text-muted-foreground">{t('calendar.monthEmpty')}</p>
      ) : (
        <ol className="space-y-4" aria-labelledby={titleId}>
          {monthDaysWithItems.map((day) => (
            <li key={day} aria-labelledby={`${idPrefix}-day-${day}`} className="space-y-1">
              <h5
                id={`${idPrefix}-day-${day}`}
                className={`text-sm font-semibold ${day === today ? 'text-primary' : ''}`}
              >
                {dayName(day)} · {formatDay(day)}
              </h5>
              <DayItemList items={byDay.get(day) ?? []} onOpenSubject={onOpenSubject} />
            </li>
          ))}
        </ol>
      )}

      <Legend />
    </div>
  );
}
